import { useState } from 'react';

import type { Dart } from '@/core/schemas';
import { sumDarts } from '@/core/engine/x01/turn';
import { Button } from '@/shared/ui/Button';
import { cn } from '@/shared/utils/cn';

interface DartEntryProps {
  remaining: number;
  disabled: boolean;
  onSubmit: (points: number, finishedOnDouble: boolean, darts: Dart[]) => void;
}

type Multiplier = 1 | 2 | 3;

const SEGMENTS = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15, 16, 17, 18, 19, 20];

/**
 * Detailed entry: up to three darts, each a segment × multiplier. The turn is
 * submitted after three darts or manually; whether it finished on a double is
 * read from the last dart, which is what a double-out checkout needs.
 */
export function DartEntry({ remaining, disabled, onSubmit }: DartEntryProps) {
  const [darts, setDarts] = useState<Dart[]>([]);
  const [multiplier, setMultiplier] = useState<Multiplier>(1);

  const total = sumDarts(darts);

  const addDart = (segment: number) => {
    if (disabled || darts.length >= 3) {
      return;
    }
    const mult: Multiplier = segment === 25 && multiplier === 3 ? 2 : multiplier;
    setDarts([...darts, { segment, multiplier: mult }]);
    setMultiplier(1);
  };

  const submit = () => {
    const finishedOnDouble = darts.at(-1)?.multiplier === 2;
    onSubmit(total, finishedOnDouble, darts);
    setDarts([]);
    setMultiplier(1);
  };

  return (
    <div className="flex flex-col gap-3">
      <div className="flex items-center justify-between gap-3">
        <div className="flex gap-2">
          {[0, 1, 2].map((i) => {
            const dart = darts[i];
            return (
              <span
                key={i}
                className="border-line bg-surface-raised flex h-10 w-14 items-center justify-center rounded-lg border font-mono text-sm"
              >
                {dart === undefined
                  ? '–'
                  : `${dart.multiplier === 2 ? 'D' : dart.multiplier === 3 ? 'T' : ''}${dart.segment === 25 ? 'Bull' : dart.segment}`}
              </span>
            );
          })}
        </div>
        <span className="font-mono text-2xl font-bold tabular-nums">{total}</span>
      </div>

      <div className="grid grid-cols-3 gap-2">
        {(
          [
            [1, 'Single'],
            [2, 'Double'],
            [3, 'Triple'],
          ] as const
        ).map(([m, label]) => (
          <button
            key={m}
            type="button"
            disabled={disabled}
            onClick={() => setMultiplier(m)}
            className={cn(
              'touch-target rounded-lg border py-2 text-sm font-semibold',
              multiplier === m
                ? 'bg-brand text-brand-fg border-transparent'
                : 'bg-surface border-line',
            )}
          >
            {label}
          </button>
        ))}
      </div>

      <div className="grid grid-cols-5 gap-1.5">
        {SEGMENTS.map((s) => (
          <button
            key={s}
            type="button"
            disabled={disabled || darts.length >= 3}
            onClick={() => addDart(s)}
            className="bg-surface border-line hover:bg-surface-raised rounded-lg border py-2.5 text-sm disabled:opacity-40"
          >
            {s}
          </button>
        ))}
        <button
          type="button"
          disabled={disabled || darts.length >= 3}
          onClick={() => addDart(25)}
          className="bg-surface border-line hover:bg-surface-raised rounded-lg border py-2.5 text-sm disabled:opacity-40"
        >
          Bull
        </button>
        <button
          type="button"
          disabled={disabled || darts.length >= 3}
          onClick={() => addDart(0)}
          className="bg-surface border-line hover:bg-surface-raised col-span-2 rounded-lg border py-2.5 text-sm disabled:opacity-40"
        >
          Daneben
        </button>
        <button
          type="button"
          disabled={disabled || darts.length === 0}
          onClick={() => setDarts(darts.slice(0, -1))}
          className="bg-surface border-line hover:bg-surface-raised rounded-lg border py-2.5 text-sm disabled:opacity-40"
        >
          ←
        </button>
      </div>

      <Button onClick={submit} disabled={disabled} className="w-full">
        Aufnahme übernehmen ({total}) · Rest {remaining}
      </Button>
    </div>
  );
}
